import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrgStore } from '@/stores/org-store';
import { updateA2AIpAllowlist } from '@/services/a2a-ip-allowlist/update-a2a-ip-allowlist';
import { UpdateA2AIpAllowlistRequest } from '@/services/a2a-ip-allowlist/a2a-ip-allowlist-types';
import { A2A_IP_ALLOWLIST_QUERY_KEY } from '@/hooks/queries/use-get-a2a-ip-allowlist';

export const useUpdateA2AIpAllowlist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateA2AIpAllowlistRequest) =>
      updateA2AIpAllowlist(request),
    // Capture the org the save was issued against, so a mid-flight org switch
    // can't write this response onto a different org's cache entry.
    onMutate: () => ({
      orgId: useOrgStore.getState().currentOrg?.orgid,
    }),
    // Seed the cache with the server's response instead of invalidating.
    // invalidateQueries kicks off a background refetch that isLoading does
    // not cover, so when it lands the page's sync effect overwrites whatever
    // the admin typed in the meantime. The PUT response is already the
    // authoritative normalized config, so there is nothing to refetch.
    //
    // setQueryData with the FULL key, never setQueriesData with the prefix:
    // the read hook keys by orgId, so a prefix write lands on every org's
    // entry and (with the 5m staleTime) another org would render this org's
    // CIDRs -- which a subsequent Save could then persist there.
    onSuccess: (data, _request, context) => {
      if (!context?.orgId) return;
      queryClient.setQueryData(
        [A2A_IP_ALLOWLIST_QUERY_KEY, context.orgId],
        data,
      );
    },
  });
};
